import React, { useEffect, useState } from "react";
import { Table, Input, Button, Space, Popconfirm, message, Tag, Tooltip, Typography } from "antd";
import { EditOutlined, DeleteOutlined, FileTextOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import dayjs from "dayjs";
import startCase from "lodash/startCase";
import RequirePermission from "../components/Common/RequirePermission";
import CampaignForm from "../components/Campaigns/CampaignForm";
import CampaignReportModal from "../components/Campaigns/CampaignReportModal";

const { Title } = Typography;

const Campaigns = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [reportCampaign, setReportCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createPermission, setCreatePermission] = useState(false);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);

  const [currentPage, setCurrentPage] = useState(Number(queryParams.get("page")) || 1);
  const [pageSize, setPageSize] = useState(Number(queryParams.get("limit")) || 10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterText, setFilterText] = useState(queryParams.get("filter") || "");

  const setQueryParams = (params) => {
    const searchParams = new URLSearchParams(params);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const handleSearch = (e) => {
    setFilterText(e.target.value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleEdit = (record) => {
    setEditingCampaign(record);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns/${id}`
      );
      if (response.data.data.updated_row_count === 1) {
        message.success("Campaign deleted");
        await fetchData();
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to delete campaign");
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/campaigns`,
        { params: { filter: filterText, limit: pageSize, page: currentPage } }
      );
      setCampaigns(response.data.data.data);
      setTotalRecords(response.data.data.total);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      message.error("Error fetching campaigns");
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("userData"));
    const permissions = storedUser?.permissions || {};
    setCreatePermission(parseInt(permissions["signage.campaigns_create"]) === 1);
    setEditPermission(parseInt(permissions["signage.campaigns_edit"]) === 1);
    setDeletePermission(parseInt(permissions["signage.campaigns_delete"]) === 1);
  }, []);

  useEffect(() => {
    setQueryParams({ filter: filterText, limit: pageSize, page: currentPage });
    const timer = setTimeout(fetchData, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filterText]);

  const columns = [
    { title: "Title", dataIndex: "title" },
    {
      title: "Package",
      dataIndex: "package",
      render: (text) => <Tag color={text === "platinum" ? "purple" : "gold"}>{startCase(text)}</Tag>,
    },
    {
      title: "Duration",
      render: (_, record) => (
        <span className="text-small">
          {dayjs(record.start_date).format("DD-MM-YYYY")} - {dayjs(record.end_date).format("DD-MM-YYYY")}
        </span>
      ),
    },
    {
      title: "Hours",
      render: (_, record) => (
        <span className="text-small">
          {record.start_time?.slice(0, 5)} - {record.end_time?.slice(0, 5)}
        </span>
      ),
    },
    {
      title: "Sellable Hrs/Day",
      dataIndex: "sellable_hours_duration",
    },
    {
      title: "Total Days",
      dataIndex: "total_campaign_duration",
    },
    {
      title: "Impressions/Screen/Day",
      dataIndex: "impressions_per_screen_per_day",
    },
    {
      title: "Locations",
      dataIndex: "location_count",
    },
    {
      title: "Playlists (Landscape)",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {record.campaign_playlist_landscape_title && (
            <span className="text-small">Campaign: {record.campaign_playlist_landscape_title}</span>
          )}
          {record.tablespace_playlist_landscape_title && (
            <span className="text-small">Tablespace: {record.tablespace_playlist_landscape_title}</span>
          )}
        </div>
      ),
    },
    {
      title: "Playlists (Portrait)",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {record.campaign_playlist_portrait_title && (
            <span className="text-small">Campaign: {record.campaign_playlist_portrait_title}</span>
          )}
          {record.tablespace_playlist_portrait_title && (
            <span className="text-small">Tablespace: {record.tablespace_playlist_portrait_title}</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <Tag color={text === "active" ? "green" : "red"}>{startCase(text)}</Tag>
      ),
    },
  ];

  columns.push({
    title: "Actions",
    render: (_, record) => (
      <Space>
        <Tooltip title="Report">
          <Button size="small" icon={<FileTextOutlined />} onClick={() => setReportCampaign(record)} />
        </Tooltip>
        {editPermission && (
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
        )}
        {deletePermission && (
          <Popconfirm
            title="Are you sure to delete this campaign?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        )}
      </Space>
    ),
  });

  return (
    <RequirePermission permission="signage.campaigns_view">
      <div style={{ padding: "1rem" }}>
        <Helmet>
          <title>{`${process.env.REACT_APP_NAME} | Campaigns`}</title>
        </Helmet>
        <Title level={3} style={{ marginBottom: "1rem" }}>
          Campaigns
        </Title>
        <div className="d-flex justify-content-between mb-3">
          <Input.Search
            placeholder="Search campaigns"
            value={filterText}
            onChange={handleSearch}
            style={{ width: 240 }}
          />
          {createPermission && (
            <Button
              type="primary"
              onClick={() => {
                setEditingCampaign(null);
                setIsModalOpen(true);
              }}
            >
              Add New Campaign
            </Button>
          )}
        </div>
        <Table
          loading={loading}
          columns={columns}
          dataSource={campaigns}
          rowKey="id"
          scroll={{ x: "max-content" }}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalRecords,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} campaigns`,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50", "100"],
          }}
        />
        {isModalOpen && (
          <CampaignForm
            isModalOpen={isModalOpen}
            editingCampaign={editingCampaign}
            closeModal={() => setIsModalOpen(false)}
            handleUpdateList={fetchData}
          />
        )}
        {reportCampaign && (
          <CampaignReportModal
            campaign={reportCampaign}
            closeModal={() => setReportCampaign(null)}
          />
        )}
      </div>
    </RequirePermission>
  );
};

export default Campaigns;
